import { Component } from '@theme/component';

/**
 * @typedef {object} Refs
 * @property {HTMLElement} timer
 * @property {HTMLElement} days
 * @property {HTMLElement} hours
 * @property {HTMLElement} minutes
 * @property {HTMLElement} seconds
 * @property {HTMLElement} [expiredMessage]
 *
 * @extends {Component<Refs>}
 */
class OfferCountdown extends Component {
  requiredRefs = ['timer', 'days', 'hours', 'minutes', 'seconds'];

  /** @type {number | null} */
  #intervalId = null;

  /** @type {number | null} */
  #readyFrameId = null;

  connectedCallback() {
    super.connectedCallback();
    this.#start();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#start();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#stop();
    if (this.#readyFrameId !== null) {
      window.cancelAnimationFrame(this.#readyFrameId);
      this.#readyFrameId = null;
    }
  }

  #start() {
    this.#stop();
    this.removeAttribute('data-ready');
    this.removeAttribute('data-countdown-complete');
    this.refs.timer.hidden = false;

    const wrap = this.refs.timer.parentElement;
    if (wrap) wrap.hidden = false;

    if (this.refs.expiredMessage) {
      this.refs.expiredMessage.hidden = true;
    }

    const target = this.#getTargetDate();
    if (!target) {
      this.setAttribute('data-countdown-disabled', '');
      this.#markReady();
      return;
    }

    this.removeAttribute('data-countdown-disabled');
    this.#tick(target);
    this.#intervalId = window.setInterval(() => this.#tick(target), 1000);
    this.#markReady();
  }

  #stop() {
    if (this.#intervalId === null) return;

    window.clearInterval(this.#intervalId);
    this.#intervalId = null;
  }

  #getTargetDate() {
    const rawValue = this.dataset.endAt?.trim();
    if (!rawValue) return null;

    const match = rawValue.match(
      /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})([+-]\d{2}:\d{2})$/
    );
    if (!match) return null;

    const [, yearRaw, monthRaw, dayRaw, hourRaw, minuteRaw, secondRaw] = match;
    const year = Number.parseInt(yearRaw, 10);
    const month = Number.parseInt(monthRaw, 10);
    const day = Number.parseInt(dayRaw, 10);
    const hour = Number.parseInt(hourRaw, 10);
    const minute = Number.parseInt(minuteRaw, 10);
    const second = Number.parseInt(secondRaw, 10);

    if (month < 1 || month > 12) return null;
    if (hour > 23 || minute > 59 || second > 59) return null;

    const maxDay = new Date(Date.UTC(year, month, 0)).getUTCDate();
    if (day < 1 || day > maxDay) return null;

    const timestamp = Date.parse(rawValue);
    if (Number.isNaN(timestamp)) return null;

    return new Date(timestamp);
  }

  /**
   * @param {Date} target
   */
  #tick(target) {
    const diff = target.getTime() - Date.now();

    if (diff <= 0) {
      this.#render(0, 0, 0, 0);
      this.#handleCompletion();
      return;
    }

    const totalSeconds = Math.floor(diff / 1000);
    const days = Math.floor(totalSeconds / 86400);
    const hours = Math.floor((totalSeconds % 86400) / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    this.#render(days, hours, minutes, seconds);
  }

  /**
   * @param {number} days
   * @param {number} hours
   * @param {number} minutes
   * @param {number} seconds
   */
  #render(days, hours, minutes, seconds) {
    this.refs.days.textContent = this.#format(days);
    this.refs.hours.textContent = this.#format(hours);
    this.refs.minutes.textContent = this.#format(minutes);
    this.refs.seconds.textContent = this.#format(seconds);
  }

  #handleCompletion() {
    this.#stop();
    this.setAttribute('data-countdown-complete', '');

    if (this.dataset.hideWhenComplete === 'true') {
      const wrap = this.refs.timer.parentElement;
      if (wrap) wrap.hidden = true;
      else this.refs.timer.hidden = true;
      return;
    }

    this.refs.timer.hidden = true;

    if (this.refs.expiredMessage) {
      this.refs.expiredMessage.hidden = false;
    }
  }

  /**
   * @param {number} value
   */
  #format(value) {
    return String(value).padStart(2, '0');
  }

  #markReady() {
    if (this.#readyFrameId !== null) {
      window.cancelAnimationFrame(this.#readyFrameId);
    }

    this.#readyFrameId = window.requestAnimationFrame(() => {
      this.#readyFrameId = window.requestAnimationFrame(() => {
        this.setAttribute('data-ready', '');
        this.#readyFrameId = null;
      });
    });
  }
}

if (!customElements.get('offer-countdown-component')) {
  customElements.define('offer-countdown-component', OfferCountdown);
}
